<template>
    <!-- Escape and a backdrop click stay enabled. Forcing a choice would be a bigger change than
         this PR intends, and it would strand the user when the save request fails: the buttons
         only close on a 204, and there is no other way out of a modal.
         reserve-mobile-nav-space is off because the pages this renders on have no mobile nav bar
         to sit above. -->
    <BaseDialog v-model:open="showBanner" :title="t('components.cookieBanner.title')" width="narrow" :show-close="false" :reserve-mobile-nav-space="false">
        <p class="details md:body-small text-ink-muted">
            {{ t("components.cookieBanner.body") }}
            <!-- eslint-disable @intlify/vue-i18n/no-raw-text -- punctuation: the period that closes the "Learn more here" sentence -->
            <a class="font-semibold underline text-button-tertiary-text" href="https://www.valence.co/other/privacy#cookies" target="_blank">{{
                t("components.cookieBanner.learnMore")
            }}</a
            >.
            <!-- eslint-enable @intlify/vue-i18n/no-raw-text -- end of the punctuation exemption -->
        </p>
        <template #footer>
            <BaseButton theme="tertiary" data-testid="reject-cookie-button" @click="rejectCookies">{{ t("components.cookieBanner.onlyNecessary") }}</BaseButton>
            <BaseButton theme="primary" data-testid="accept-cookie-button" @click="acceptAllCookies">{{ t("components.cookieBanner.acceptAll") }}</BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { usePage } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const showBanner = ref(false);

onMounted(async () => {
    // Native apps don't need cookie consent banners — privacy is handled
    // via App Store privacy labels and iOS App Tracking Transparency.
    if (usePage().props?.is_mobile_app) {
        return;
    }
    const cookieConsent = localStorage.getItem("cookie_consent");
    if (cookieConsent === "persisted") {
        return;
    }

    const response = await fetch("/accounts/cookie-preferences/" + (cookieConsent || ""), {
        method: "GET",
        headers: { "X-CSRFToken": getCookie("csrftoken") },
    });
    if (response.status != 200) {
        console.error("Failed to get cookie preferences");
        return;
    }
    const data = await response.json();
    if (!data.cookie_preferences) {
        showBanner.value = true;
        return;
    }

    if (data.cookie_preferences === "updated" || !cookieConsent) {
        localStorage.setItem("cookie_consent", "persisted");
    }
});

const updatePreferences = async (choice) => {
    const response = await fetch("/accounts/cookie-preferences/", {
        method: "PUT",
        body: JSON.stringify({ choice, method: "banner" }),
        headers: { "X-CSRFToken": getCookie("csrftoken"), "Content-Type": "application/json" },
    });
    if (response.status != 204) {
        console.error("Failed to update cookie preferences");
        return null;
    }
    localStorage.setItem("cookie_consent", `?consent_method=banner&consent_choice=${choice}&consent_at=${new Date().toISOString()}`);
    return response;
};

async function rejectCookies() {
    const response = await updatePreferences("performance");
    if (response) {
        showBanner.value = false;
    }
}

async function acceptAllCookies() {
    const response = await updatePreferences("all");
    if (response) {
        showBanner.value = false;
    }
}
</script>
